import * as React from 'react'
import TextField from '@mui/material/TextField'
import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogTitle from '@mui/material/DialogTitle'
import {
  Autocomplete,
  Alert,
  Box,
  Button,
  Divider,
  Grid,
  Tooltip,
  TooltipProps,
  styled,
  tooltipClasses,
} from '@mui/material'
import { ClientsContext, clientErrorMessage } from '../context/clientsContext'
import { CasesContext, SavedCase } from '../context/casesContext'
import { DialogsContext } from '../context/dialogsContext'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { CaseType, User, WaiverType, caseTypeOptions, CaseTypeType } from '../types'
import { LoadingButton } from '@mui/lab'
import WaiverCheckboxDialog from './WaiverCheckboxDialog.tsx'
import { WaiverContext } from '../context/waiversContext'
import { method } from 'lodash'
import ConfirmDialog from './ConfirmDialog'

// Healer Waiver
const healerCaseWaiverId = 3;

export default function SaveCaseDialog() {
  const { showCaseDialog, caseDialogState, showSessionDialog } = React.useContext(DialogsContext);
  const { saveCase } = React.useContext(CasesContext);
  const { clients, clientsLoading, clientsError, scopeKey, resendWaiver } = React.useContext(ClientsContext);
  const [loading, setLoading] = React.useState(false);
  const { mainWaivers } = React.useContext(WaiverContext);
  const [healerCaseWaiver, setHealerCaseWaiver] = React.useState<WaiverType | null>(null);
  const [createSessions, setCreateSession] = React.useState(false);
  const [savedCaseClient, setSavedCaseClient] = React.useState<User | null>(null);
  const [createdCase, setCreatedCase] = React.useState<SavedCase | null>(null);
  const [saveError, setSaveError] = React.useState<string | null>(null);
  const [invitationFailed, setInvitationFailed] = React.useState(false);
  const [resending, setResending] = React.useState(false);
  const [resendError, setResendError] = React.useState<string | null>(null);
  const recoveryRef = React.useRef({ scopeKey });
  if (recoveryRef.current.scopeKey !== scopeKey) recoveryRef.current = { scopeKey };
  const sessionKey = `${scopeKey}:${caseDialogState.show}:${caseDialogState.clientId}`;
  const sessionRef = React.useRef({ key: sessionKey });
  if (sessionRef.current.key !== sessionKey) sessionRef.current = { key: sessionKey };
  const previousScope = React.useRef(scopeKey);
  const submitting = React.useRef(false);
  const mounted = React.useRef(true);
  const initializedSession = React.useRef<{ key: string } | null>(null);


  type AddCaseType = {
    name: string;
    client: User | null;
    type: CaseTypeType | null;
    accepted_waiver: boolean;
  }

  React.useEffect(() => {
    if (mainWaivers) {
      const healingWaiver = mainWaivers.find((mw) => mw.id === healerCaseWaiverId);
      setHealerCaseWaiver(healingWaiver || null);
    }
  }, [mainWaivers])

  const { control, setValue, getValues } = useForm<AddCaseType>({ defaultValues: { name: '', client: null, type: null, accepted_waiver: false } });

  const closeRecovery = () => {
    recoveryRef.current = { scopeKey };
    setResending(false);
    setResendError(null);
    setCreateSession(false);
  };

  const handleClose = () => {
    if (submitting.current) return;
    showCaseDialog({ show: false });
    setValue('name', '');
    setValue('type', null);
    setValue('client', null);
    setValue('accepted_waiver', false);
    setLoading(false);
    setSaveError(null);
  }

  React.useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  React.useEffect(() => {
    submitting.current = false;
    setLoading(false);
    setSaveError(null);
    if (previousScope.current !== scopeKey) {
      handleClose();
    }
    if (previousScope.current !== scopeKey || caseDialogState.show) {
      closeRecovery();
      setCreatedCase(null);
      setInvitationFailed(false);
    }
    previousScope.current = scopeKey;
  }, [sessionKey]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (submitting.current) return;
    const session = sessionRef.current;
    const { client, name, type, accepted_waiver } = getValues();
    if (clientsLoading || clientsError) {
      setSaveError('Wait for the client list to load before creating a case.');
      return;
    }
    if (client && !clients.some(candidate => candidate.id === client.id)) {
      setSaveError('This client is no longer available. Select another client.');
      return;
    }
    if (!client || !type || !accepted_waiver) return;
    submitting.current = true;
    setLoading(true);
    setSaveError(null);
    try {
      const newCase = {
        name: name,
        client_id: client.id,
        type: type.label.toLowerCase(),
        accepted_waiver_id: healerCaseWaiverId
      }
      const result = await saveCase(newCase);
      if (!mounted.current || sessionRef.current !== session) return;
      submitting.current = false;
      setCreatedCase(result);
      setInvitationFailed(result.notification_status === 'failed' || result.notification_status === 'pending');
      setSavedCaseClient(client);
      handleClose();
      setCreateSession(true);
    } catch (error) {
      if (mounted.current && sessionRef.current === session) {
        setSaveError(clientErrorMessage(error, 'Unable to confirm the case was saved. Refresh your cases before retrying.'));
      }
    } finally {
      if (mounted.current && sessionRef.current === session) {
        submitting.current = false;
        setLoading(false);
      }
    }
  }

  React.useEffect(() => {
    if (!caseDialogState.show || clientsLoading || clientsError) return;
    if (initializedSession.current !== sessionRef.current) {
      setValue('client', clients.find(candidate => candidate.id === caseDialogState.clientId) || null);
      initializedSession.current = sessionRef.current;
      return;
    }
    const selected = getValues('client');
    if (selected && !clients.some(candidate => candidate.id === selected.id)) {
      setValue('client', null);
      setSaveError('This client is no longer available. Select another client.');
    }
  }, [sessionKey, clients, clientsLoading, clientsError])


  // React.useEffect(() => {
  //   setLoading(false);
  // }, [savedCase, savedCaseError]);

  // React.useEffect(() => {
  //   if (caseDialogState.clientId) {
  //     setValues({

  //     })
  //   }
  // }, [caseDialogState])


  // const getInitialValue = () => {
  //   if (caseDialogState.clientId) {
  //     const client = clients?.find((client) => client.id === caseDialogState.clientId);
  //     if (client) {
  //       return {
  //         id: undefined,
  //         name: undefined,
  //         clientId: client.id,
  //         client: client,
  //         type: undefined,
  //         description: undefined,
  //       }
  //     }
  //   }
  //   return {
  //     id: undefined,
  //     name: undefined,
  //     clientId: undefined,
  //     client: undefined,
  //     type: undefined,
  //     description: undefined,
  //   }
  // }

  // const isEmpty = (val: string) => { }

  // const LightTooltip = styled(({ className, ...props }: TooltipProps) => (
  //   <Tooltip {...props} classes={{ popper: className }} />
  // ))(({ theme }) => ({
  //   [`& .${tooltipClasses.tooltip}`]: {
  //     backgroundColor: theme.palette.common.white,
  //     color: 'rgba(0, 0, 0, 0.87)',
  //     boxShadow: theme.shadows[1],
  //     fontSize: 18,
  //     textAlign: 'center',
  //   },
  // }));

  return (
    <div>
      <Dialog open={!!caseDialogState.show} onClose={handleClose} keepMounted={false}>
        <DialogTitle>{'Create New Case'}</DialogTitle>
        <Divider />
        <DialogContent>
          {saveError && <Alert severity='error'>{saveError}</Alert>}
          <DialogContentText>
            If the clients email was entered when they were added to Prana Records they will receive a request through email to fill out a healing waiver and other details.
          </DialogContentText>
          <Box m={2} />
          <form onSubmit={(e) => handleSubmit(e)}>
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <TextField
                  fullWidth
                  label="Case Name (avoid using client name)"
                  variant="outlined"
                  {...field}
                  required
                />
              )}
            />
            <Box m={1} />
            <Controller
              control={control}
              name='client'
              rules={{ required: true }}
              render={({ field: { onChange, value } }) => (
                <Autocomplete
                  noOptionsText={'No Clients'}
                  onChange={(event, client) => {
                    onChange(client)
                  }}
                  value={value && (clientsLoading || clients.some(candidate => candidate.id === value.id)) ? value : null}
                  loading={clientsLoading}
                  options={clientsLoading && value ? [value] : clients}
                  getOptionLabel={(client) => (client ? client.full_name || client.email : '')}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label='Client'
                      margin='normal'
                      variant='outlined'
                      required
                    />
                  )}
                />
              )}
            />
            <Controller
              control={control}
              name='type'
              rules={{ required: true }}
              render={({ field: { onChange, value } }) => (
                <Autocomplete
                  noOptionsText={'No Types'}
                  onChange={(event, type) => onChange(type)}
                  value={value}
                  options={caseTypeOptions}
                  getOptionLabel={(type) => (type ? type.label : '')}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label='Case Type'
                      margin='normal'
                      variant='outlined'
                      required
                    />
                  )}
                />
              )}
            />
            <Box m={2} />
            <WaiverCheckboxDialog
              title={healerCaseWaiver ? healerCaseWaiver.title : ''}
              copy={healerCaseWaiver ? healerCaseWaiver.copy : ''}
              label={'I Accept Healing Waiver'}
              formMethods={{ control, setValue, getValues }}
              name={'accepted_waiver'}
              required={true} />
            <Box m={2} />
            <Grid item>
              <LoadingButton loading={loading} disabled={clientsLoading || !!clientsError} variant='contained' type='submit' color='primary' fullWidth>
                Create
              </LoadingButton>
              <Box m={2} />
              <Button disabled={loading} variant='text' onClick={handleClose} color='primary' fullWidth>
                Cancel
              </Button>
            </Grid>
          </form>
        </DialogContent>
      </Dialog>
      <Dialog open={createSessions && invitationFailed} onClose={closeRecovery}>
        <DialogTitle>Case saved</DialogTitle>
        <DialogContent>
          <Alert severity='warning'>The case was saved, but its invitation was not confirmed. Do not create another case.</Alert>
          {resendError && <Alert severity='error'>{resendError}</Alert>}
          <LoadingButton loading={resending} onClick={async () => {
            if (!createdCase || resending) return;
            const recovery = recoveryRef.current;
            setResending(true);
            setResendError(null);
            try {
              const sent = await resendWaiver(createdCase.id);
              if (!mounted.current || recoveryRef.current !== recovery) return;
              if (sent) setInvitationFailed(false);
              else setResendError('Unable to send the invitation. Please try again.');
            } catch {
              if (mounted.current && recoveryRef.current === recovery) {
                setResendError('Unable to send the invitation. Please try again.');
              }
            } finally {
              if (mounted.current && recoveryRef.current === recovery) setResending(false);
            }
          }}>Resend invitation</LoadingButton>
          <Button onClick={closeRecovery}>Done</Button>
        </DialogContent>
      </Dialog>
      <ConfirmDialog
        open={createSessions && !invitationFailed}
        title="Create Session"
        message="Would you like to create a session for your new case?"
        onConfirm={() => {
          if (createdCase && savedCaseClient) {
            showSessionDialog({ show: true, caseId: createdCase.id, clientId: savedCaseClient.id });
          } else {
            showSessionDialog({ show: true });
          }
          setCreateSession(false);
        }}
        onCancel={() => {
          setCreateSession(false);
        }}
      />
    </div>
  )
}
